import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { array, func, string as propString } from 'prop-types';
import useForm from '../../../common/src/legacy/hooks/useForm';
import { object } from 'yup';
import {
  string,
  pastYear,
} from '../../../common/src/constants/validationSchemas';

import {
  DashedButton,
  RoundedButton,
  IconActionPlus,
  TextInput,
  ActionButton,
  RiskObjectDisplay,
  IconActionClose,
  DataBlock,
  H4,
  Body,
  SearchSelectInput,
  TertiaryButton,
  Tooltip,
  IconOthersFilled,
  IconTheftFilled,
  IconCarGlassBreakage,
  IconCarCollision,
} from 'wg-fe-ui';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentlyEditing } from './redux/slices/proFlowStorageSlice';

export const CAUSES_OPTIONS = [
  { value: 'COLLISION_IN_FAULT', label: 'COLLISION_IN_FAULT' },
  { value: 'COLLISION_NOT_IN_FAULT', label: 'COLLISION_NOT_IN_FAULT' },
  { value: 'GLASS_BREAKAGE', label: 'GLASS_BREAKAGE' },
  { value: 'THEFT_BURGLARY', label: 'THEFT_BURGLARY' },
  { value: 'OTHER', label: 'OTHER' },
];

export const LOSS_AMOUNT_OPTIONS = [
  { value: 'LESS_THAN_500', label: 'LESS_THAN_500' },
  { value: 'BETWEEN_500_AND_2000', label: 'BETWEEN_500_AND_2000' },
  { value: 'BETWEEN_2000_AND_5000', label: 'BETWEEN_2000_AND_5000' },
  { value: 'BETWEEN_5000_AND_10000', label: 'BETWEEN_5000_AND_10000' },
  { value: 'MORE_THAN_10000', label: 'MORE_THAN_10000' },
];

const ProFlowPiaClaims = ({ onChange, values: claims, title, description }) => {
  const dispatch = useDispatch();
  const currentlyEditing =
    useSelector((state) => state.proFlowStorage?.currentlyEditing) || [];
  const [addClaimVisible, setAddClaimVisible] = useState(false);
  const [buttonVisible, setButtonVisible] = useState(true);
  const { t } = useTranslation();

  const { handleChange, errors, handleSubmit, values, resetForm } = useForm({
    validationSchema: object().shape({
      cause: string.required,
      loss_amount: string.required,
      year: pastYear.required,
    }),
  });

  const handleIcons = (type) => {
    switch (type) {
      case 'COLLISION_IN_FAULT':
      case 'COLLISION_NOT_IN_FAULT':
        return <IconCarCollision color="#fff" />;
      case 'GLASS_BREAKAGE':
        return <IconCarGlassBreakage color="#fff" />;
      case 'THEFT_BURGLARY':
        return <IconTheftFilled color="#fff" />;
      default:
        return <IconOthersFilled color="#fff" />;
    }
  };

  const submitClaim = () => {
    const payload = {
      cause: values.cause,
      loss_amount: values.loss_amount,
      year: values.year,
    };

    onChange(claims ? [...claims, payload] : [payload]);
    resetForm();
  };

  const handleAddClaim = () => {
    setAddClaimVisible(true);
    setButtonVisible(false);
  };

  const handleRemoveClaim = (claimId) => {
    onChange(claims?.filter((claim) => claim.itemId !== claimId));
  };

  return (
    <>
      <Label>
        {title}{' '}
        {description && (
          <StyledTooltip toggleIcon="?">{description}</StyledTooltip>
        )}
      </Label>
      <Container>
        {claims?.map((claim) => {
          return (
            <RiskObjectDisplay
              key={claim.itemid}
              hoverButton={
                <RoundedButton
                  icon={<IconActionClose color="black" size={40} />}
                  iconType="fill"
                  size="large"
                  type="danger"
                />
              }
              hoverContent={t('Remove claim')}
              icon={handleIcons(claim.cause)}
              onClick={() => handleRemoveClaim(claim.itemId)}
              title={t(claim.cause)}
            >
              <DataBlock>
                <Body light smaller>
                  {t('Loss amount')}
                </Body>
                <H4>{t(claim.loss_amount)}</H4>
              </DataBlock>
              <DataBlock>
                <Body light smaller>
                  {t('What year did it occur')}
                </Body>
                <H4>{t(claim.year)}</H4>
              </DataBlock>
            </RiskObjectDisplay>
          );
        })}
        {addClaimVisible && (
          <AddClaimContainer>
            <TopContainer>
              {values.cause ? (
                handleIcons(values.cause)
              ) : (
                <IconOthersFilled color="#fff" />
              )}
              <StyledClaimsSearchSelectInput
                isClearable
                name="cause"
                onSelected={handleChange}
                placeholder={t('Claim cause')}
                options={CAUSES_OPTIONS.map(({ value, label }) => ({
                  value,
                  label: t(label),
                }))}
                error={errors.cause}
              />
            </TopContainer>
            <BottomContainer>
              <InputWrapper>
                <Body light smaller>
                  {t('Loss amount')}
                </Body>
                <StyledLossAmountInput
                  isClearable
                  name="loss_amount"
                  onSelected={handleChange}
                  placeholder={t('Loss amount')}
                  options={LOSS_AMOUNT_OPTIONS.map(({ value, label }) => ({
                    value,
                    label: t(label),
                  }))}
                  error={errors.loss_amount}
                />
              </InputWrapper>
              <InputWrapper>
                <Body light smaller>
                  {t('What year did it occur')}
                </Body>
                <StyledTextInput
                  isClearable
                  name="year"
                  onChange={handleChange}
                  placeholder={t('YYYY')}
                  type="number"
                  error={errors.year}
                />
              </InputWrapper>
              <ActionButtons>
                <ActionButton
                  onClick={() =>
                    handleSubmit(() => {
                      submitClaim();
                      setAddClaimVisible(false);
                      setButtonVisible(true);
                      dispatch(
                        setCurrentlyEditing(
                          currentlyEditing.filter((name) => name !== 'claims'),
                        ),
                      );
                    })
                  }
                  fullwidth
                >
                  {t('Save claim')}
                </ActionButton>
                <TertiaryButton
                  onClick={() => {
                    resetForm();
                    setAddClaimVisible(false);
                    setButtonVisible(true);
                    dispatch(
                      setCurrentlyEditing(
                        currentlyEditing.filter((name) => name !== 'claims'),
                      ),
                    );
                  }}
                  label={t('Cancel')}
                />
              </ActionButtons>
            </BottomContainer>
          </AddClaimContainer>
        )}
        {buttonVisible && (
          <DashedButton
            button={
              <RoundedButton
                icon={<IconActionPlus color="black" size={35} />}
                iconType="fill"
                size="large"
                type="primary"
              />
            }
            label={t('Add claim')}
            onClick={() => {
              handleAddClaim();
              setCurrentlyEditing([...currentlyEditing, 'claims']);
            }}
            styleName="normal"
          />
        )}
      </Container>
    </>
  );
};

const StyledTooltip = styled(Tooltip)`
  margin-left: 1rem;
  div {
    width: 40rem;
  }
`;

const Label = styled.label`
  align-items: center;
  color: #8990a7;
  display: flex;
  flex-direction: row;
  font-weight: 400;
  font-size: 1.4rem;
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  button:first-child {
    margin-bottom: 1rem;
  }
`;

const InputWrapper = styled.div`
  &:first-child {
    margin-bottom: 1rem;
  }

  &:nth-child(2) {
    margin-bottom: 2rem;
  }
`;

const StyledLossAmountInput = styled(SearchSelectInput)`
  min-height: unset;
  margin-top: 1rem;

  label {
    > div {
      margin: 0 0 1rem;
    }

    > div:last-child {
      > div > div {
        line-height: 2rem;
      }
    }
  }
  > div {
    margin: 0;
  }
`;

const StyledTextInput = styled(TextInput)`
  height: unset;

  > div {
    label {
      margin: 0 0 1rem;
    }
  }
`;

const StyledClaimsSearchSelectInput = styled(SearchSelectInput)`
  min-height: unset;
  label {
    > div {
      margin: 0;
    }

    > div:last-child {
      > div > div {
        line-height: 2rem;
      }
    }
  }
  > div {
    margin-bottom: 0;
  }
`;

const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-gap: 1rem;
  margin-top: 2.4rem;

  @media screen and (max-width: 950px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const TopContainer = styled.div`
  display: flex;
  max-height: 10rem;
  padding: 2.4rem;
  border-bottom: 1px solid #e4e4e4;

  > div {
    width: 100%;
  }

  > svg {
    background: #c4c4c4;
    border-radius: 5px;
    height: 4.35rem;
    margin-right: 1rem;
    padding: 0.75rem;
    width: 5rem;
    > path {
      fill: #ffffff;
    }
  }
`;

const BottomContainer = styled.div`
  padding: 2.4rem;
`;

const AddClaimContainer = styled.div`
  border: 0.1rem solid #f0f1f3;
  border-radius: 0.5rem;
  box-shadow: 0 0 0.1rem rgba(0, 0, 0, 0.04),
    0 0.2rem 0.6rem rgba(0, 0, 0, 0.04), 0 1rem 2rem rgba(0, 0, 0, 0.04);
`;

ProFlowPiaClaims.propTypes = {
  onChange: func.isRequired,
  values: array,
  title: propString,
  description: propString,
};

export default ProFlowPiaClaims;
